// frontend/src/pages/LandingPage/ContactPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    Mail, 
    Phone, 
    MapPin, 
    Send,
    MessageSquare,
    Clock,
    CheckCircle2
} from 'lucide-react';
import HeaderPublic from '../../components/Navigation/HeaderPublic';
import Footer from '../../components/LandingPage/Footer';
import PrimaryButton from '../../components/Common/PrimaryButton';
import { sendContactMessage } from '../../api/contactApi';

const ContactPage = () => {
    const location = useLocation();
    // Garante que a página sempre comece no topo ao carregar
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: location.state?.subject || '',
        message: location.state?.message || '',
        botcheck: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError('');

        try {
            await sendContactMessage(formData);
            setIsSubmitted(true);
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: '',
                botcheck: ''
            });
        } catch (error) {
            console.error('[ContactPage] Falha ao enviar mensagem:', error);
            setSubmitError('Não foi possível enviar agora. Tente de novo em instantes ou ligue para o telefone ao lado.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactInfo = [
        {
            icon: Mail,
            title: 'Email',
            content: 'contato@higeia.tech',
            link: 'mailto:contato@higeia.tech',
            color: 'text-blue-600',
            bgColor: 'bg-blue-50'
        },
        {
            icon: Phone,
            title: 'Telefone',
            content: '(53) 3196-0447',
            link: 'tel:+5511999999999',
            color: 'text-green-600',
            bgColor: 'bg-green-50'
        },
        {
            icon: MapPin,
            title: 'Endereço',
            content: 'Pelotas, RS - Brasil',
            link: null,
            color: 'text-purple-600',
            bgColor: 'bg-purple-50'
        },
        {
            icon: Clock,
            title: 'Horário de Atendimento',
            content: 'Segunda a Sexta: 9h às 18h',
            link: null,
            color: 'text-orange-600',
            bgColor: 'bg-orange-50'
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Público */}
            <div className="bg-white shadow-sm">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <HeaderPublic />
                </div>
            </div>

            {/* Conteúdo Principal */}
            <main className="py-16 sm:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    
                    {/* Cabeçalho */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 mb-4">
                            Entre em{' '}
                            <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                                Contato
                            </span>
                        </h1>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Estamos aqui para ajudar. Envie sua mensagem e responderemos o mais breve possível.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        
                        {/* Coluna Esquerda: Informações de Contato */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                        >
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                Informações de Contato
                            </h2>
                            <p className="text-gray-600 mb-8">
                                Prefere falar diretamente? Entre em contato através dos canais abaixo.
                            </p>

                            {/* Cards de Informação */}
                            <div className="space-y-4">
                                {contactInfo.map((info, index) => {
                                    const Icon = info.icon;
                                    return (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                                            className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${info.bgColor} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                                                    <Icon className={`h-6 w-6 ${info.color}`} />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="font-bold text-gray-900 mb-1">{info.title}</h3>
                                                    {info.link ? (
                                                        <a 
                                                            href={info.link}
                                                            className="text-gray-600 hover:text-higeia-dark transition-colors"
                                                        >
                                                            {info.content}
                                                        </a>
                                                    ) : (
                                                        <p className="text-gray-600">{info.content}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>

                            {/* Mensagem de Suporte */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.7, duration: 0.6 }}
                                className="mt-8 bg-gradient-to-r from-higeia-dark to-higeia-medium rounded-2xl p-6 text-white"
                            >
                                <div className="flex items-start gap-4">
                                    <MessageSquare className="h-6 w-6 flex-shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-bold mb-2">Suporte Prioritário</h3>
                                        <p className="text-white/90 text-sm">
                                            Clientes Premium têm acesso a suporte prioritário 24/7 via WhatsApp.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>

                        {/* Coluna Direita: Formulário de Contato */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                        >
                            <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-xl border border-gray-100">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                    Envie sua Mensagem
                                </h2>

                                {isSubmitted ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="text-center py-8"
                                    >
                                        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                                            <CheckCircle2 className="h-8 w-8 text-green-600" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                                            Mensagem Enviada!
                                        </h3>
                                        <p className="text-gray-600">
                                            Obrigado pelo contato. Responderemos em breve.
                                        </p>
                                    </motion.div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <input
                                            type="text"
                                            name="botcheck"
                                            value={formData.botcheck}
                                            onChange={handleChange}
                                            tabIndex={-1}
                                            autoComplete="off"
                                            className="hidden"
                                            aria-hidden="true"
                                        />
                                        {/* Nome */}
                                        <div>
                                            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                                                Nome Completo
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-higeia-dark focus:border-transparent transition-all duration-200"
                                                placeholder="Seu nome"
                                            />
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-higeia-dark focus:border-transparent transition-all duration-200"
                                                placeholder="seu@email.com"
                                            />
                                        </div>

                                        {/* Telefone */}
                                        <div>
                                            <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                                                Telefone (Opcional)
                                            </label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-higeia-dark focus:border-transparent transition-all duration-200"
                                                placeholder="(53) 3028-3030"
                                            />
                                        </div>

                                        {/* Assunto */}
                                        <div>
                                            <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                                                Assunto
                                            </label>
                                            <input
                                                type="text"
                                                id="subject"
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-higeia-dark focus:border-transparent transition-all duration-200"
                                                placeholder="Qual o assunto da sua mensagem?"
                                            />
                                        </div>

                                        {/* Mensagem */}
                                        <div>
                                            <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                                                Mensagem
                                            </label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                required
                                                rows={6}
                                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-higeia-dark focus:border-transparent transition-all duration-200 resize-none"
                                                placeholder="Conte-nos como podemos ajudar..."
                                            />
                                        </div>

                                        {submitError && (
                                            <p className="text-sm text-red-600" role="alert">
                                                {submitError}
                                            </p>
                                        )}

                                        {/* Botão de Envio */}
                                        <motion.div
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <PrimaryButton
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="w-full py-4 text-lg font-bold bg-higeia-dark hover:bg-higeia-medium shadow-xl"
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="inline-block animate-spin mr-2">⏳</span>
                                                        Enviando...
                                                    </>
                                                ) : (
                                                    <>
                                                        Enviar Mensagem
                                                        <Send className="h-5 w-5 ml-2" />
                                                    </>
                                                )}
                                            </PrimaryButton>
                                        </motion.div>
                                    </form>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default ContactPage;

